/**
 * VH-206: "Which machine did you use?" — one choice per exercise entry.
 *
 * Rendered ONLY when the server says `needs_equipment_choice`; this component
 * never decides whether to ask. Name plus photo, because VH-196 established a
 * location can hold two rows the athlete reads as "the same machine", and a
 * name-only choice between them is unanswerable.
 *
 * There is no "I don't remember" option: skipping IS that answer (decided
 * 2026-09-12). Tapping the selected machine again un-selects it, so a mistaken
 * tap can always return to "nobody said".
 */
import { useTranslation } from 'react-i18next'
import { candidateName } from './machineName'


export default function MachineChoice({
  candidates = [],
  value = null,
  onChange,
  disabled = false,
  testIdPrefix = 'machine-choice',
}) {
  const { t, i18n } = useTranslation()

  return (
    <fieldset className="px-4 pt-3" data-testid={testIdPrefix} disabled={disabled}>
      <legend className="text-sm font-medium text-gray-900">
        {t('machineChoice.question')}
      </legend>
      <p className="text-xs text-gray-500 mt-0.5">{t('machineChoice.skipHint')}</p>
      <div className="mt-2 grid grid-cols-2 gap-2">
        {candidates.map((c) => {
          const selected = value === c.id
          const name = candidateName(c, i18n?.language)
          return (
            <button
              key={c.id}
              type="button"
              aria-pressed={selected}
              disabled={disabled}
              onClick={() => onChange?.(selected ? null : c.id)}
              data-testid={`${testIdPrefix}-${c.id}`}
              className={`flex flex-col items-stretch rounded-lg border p-2 text-left text-sm disabled:opacity-60 ${
                selected
                  ? 'border-primary-600 ring-2 ring-primary-500 bg-primary-50'
                  : 'border-outline-variant hover:bg-gray-100'
              }`}
            >
              {c.photo_url ? (
                <img
                  src={c.photo_url}
                  alt={name}
                  loading="lazy"
                  className="h-20 w-full rounded object-cover bg-gray-100"
                />
              ) : (
                <div className="h-20 w-full rounded bg-gray-100 flex items-center justify-center text-xs text-gray-400">
                  {t('machineChoice.noPhoto')}
                </div>
              )}
              <span className="mt-1 break-words leading-snug text-gray-900">{name}</span>
            </button>
          )
        })}
      </div>
    </fieldset>
  )
}
